import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Globe, MapPin, RefreshCw, ScanLine, Search, Monitor, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { BACKEND_URL } from './constants';
import { getToken } from '../../services/tokenService';

export default function AuditVerifications() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterDocId, setFilterDocId] = useState('');
  const [filterIp, setFilterIp] = useState('');
  const [filterFound, setFilterFound] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const headers = { Authorization: `Bearer ${getToken()}` };

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      if (filterDocId) params.append('document_id', filterDocId);
      if (filterIp) params.append('ip_address', filterIp);
      if (filterFound) params.append('document_trouve', filterFound);
      const res = await axios.get(`${BACKEND_URL}/api/audit/verifications?${params}`, { headers });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
      setStats(res.data.stats || null);
    } catch { toast.error('Erreur chargement audit'); }
    finally { setLoading(false); }
  }, [page, filterDocId, filterIp, filterFound]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return d; }
  };

  const formatDateShort = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };

  const _formatTime = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="space-y-4" data-testid="audit-verifications">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
            <ScanLine className="h-4 w-4 text-indigo-500 shrink-0" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total scans</p>
              <p className="text-lg font-bold text-gray-900">{stats.total_verifications}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
            <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Trouves</p>
              <p className="text-lg font-bold text-emerald-700">{stats.documents_trouves}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Non trouves</p>
              <p className="text-lg font-bold text-red-600">{stats.documents_non_trouves}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
            <Globe className="h-4 w-4 text-amber-500 shrink-0" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">IPs uniques</p>
              <p className="text-lg font-bold text-gray-900">{stats.ips_uniques}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text" placeholder="Filtrer par ID document..." value={filterDocId}
            onChange={e => { setFilterDocId(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            data-testid="audit-filter-doc"
          />
        </div>
        <div className="relative flex-1 max-w-xs">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text" placeholder="Filtrer par IP..." value={filterIp}
            onChange={e => { setFilterIp(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            data-testid="audit-filter-ip"
          />
        </div>
        <select
          value={filterFound} onChange={e => { setFilterFound(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          data-testid="audit-filter-found"
        >
          <option value="">Tous</option>
          <option value="true">Trouves</option>
          <option value="false">Non trouves</option>
        </select>
        <Button variant="outline" size="sm" onClick={loadAudit} data-testid="audit-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Count */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-sm text-gray-500">{total} verification(s)</span>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-indigo-400" /></div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <ScanLine className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-700">Aucune verification enregistree</p>
          <p className="text-sm mt-1 text-gray-400">Les scans QR code apparaitront ici</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="audit-list">
          {entries.map((e, i) => {
            const auditId = `audit-${i}`;
            const isExpanded = expandedIds.has(auditId);
            const geo = e.geolocalisation || {};
            const loc = [geo.ville, geo.region, geo.pays].filter(Boolean).join(', ');
            const ua = e.user_agent || '';
            const _shortUa = ua.length > 40 ? ua.substring(0, 40) + '...' : ua;

            return (
              <div
                key={auditId}
                className={cn(
                  "rounded-lg border transition-all duration-150",
                  isExpanded ? (e.document_trouve ? "border-emerald-200" : "border-red-200") + " shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
                data-testid={`audit-row-${i}`}
              >
                {/* Compact row */}
                <button onClick={() => toggleExpand(auditId)} className="flex items-center gap-3 px-4 py-2.5 w-full text-left" data-testid={`audit-toggle-${i}`}>
                  <ScanLine className={cn("h-4 w-4 shrink-0", e.document_trouve ? "text-emerald-500" : "text-red-500")} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-mono text-xs truncate", isExpanded ? "text-indigo-700" : "text-gray-700")}>{e.document_id}</p>
                    <p className="text-[10px] text-gray-400 truncate">{e.type_document || 'Document'}</p>
                  </div>
                  {e.document_trouve ? (
                    <Badge className="bg-emerald-700 text-white text-[10px] font-semibold shrink-0"><CheckCircle className="h-3 w-3 mr-0.5" />Trouve</Badge>
                  ) : (
                    <Badge className="bg-red-700 text-white text-[10px] font-semibold shrink-0"><AlertTriangle className="h-3 w-3 mr-0.5" />Non trouve</Badge>
                  )}
                  <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500 shrink-0 font-mono">{e.ip_address || 'N/A'}</div>
                  <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <Calendar className="h-3 w-3" /><span>{formatDateShort(e.date_verification)}</span>
                  </div>
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-indigo-500")} />
                </button>

                {/* Expanded panel */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`audit-details-${i}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Col 1: Document */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Document</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-mono text-xs text-gray-700 break-all">{e.document_id}</p>
                          <p className="text-gray-500 text-xs">{e.type_document || 'Type non defini'}</p>
                          <div className="flex items-center gap-1.5">
                            {e.document_trouve ? (
                              <Badge className="bg-emerald-700 text-white text-[10px] font-semibold"><CheckCircle className="h-3 w-3 mr-0.5" />Document trouve</Badge>
                            ) : (
                              <Badge className="bg-red-700 text-white text-[10px] font-semibold"><AlertTriangle className="h-3 w-3 mr-0.5" />Non trouve</Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Col 2: Scan Info */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Details du scan</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Date:</span>
                            <span className="text-gray-700">{formatDate(e.date_verification)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Adresse IP:</span>
                            <span className="font-mono text-xs text-gray-700">{e.ip_address || 'N/A'}</span>
                          </div>
                          {loc && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Localisation:</span>
                              <span className="flex items-center gap-1 text-gray-700">
                                <MapPin className="h-3 w-3 text-gray-400" />{loc}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Col 3: User Agent */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Navigateur</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex items-start gap-1.5">
                            <Monitor className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
                            <p className="text-gray-600 text-xs break-all">{ua || 'Non disponible'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {page}/{totalPages} ({total} resultats)</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="audit-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="audit-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
